import React, { ReactElement, useContext, useMemo } from 'react'

import Checkbox from '../../Common/Components/Checkbox'
import Fieldset from '../../Common/Components/Fieldset'
import FormRow from '../../Common/Components/FormRow'
import Heading from '../../Common/Components/Heading'
import { Select, SelectOptions, SelectRange, SelectRangeItem } from '../../Common/Components/Selects'
import SelectMulti from '../../Common/Components/Selects/SelectMulti'
import TextInput from '../../Common/Components/TextInput'

import { ANY_VALUE_NUMBER_NUMERIC } from '../../Common/Constants/DefaultValues'

import {
    FILTER_ANSWER_TYPE_ABANDONED,
    FILTER_ANSWER_TYPE_ADVANCED_FROM,
    FILTER_ANSWER_TYPE_ADVANCED_TO,
    FILTER_ANSWER_TYPE_ANSWERED,
    FILTER_ANSWER_TYPE_NOT_ANSWERED,
    FILTER_ANSWER_TYPE_OVERFLOWED_FROM,
    FILTER_ANSWER_TYPE_OVERFLOWED_TO,
    FILTER_CALLTYPEDIRECTION_EXTERNAL_ALL,
    FILTER_CALLTYPEDIRECTION_EXTERNAL_INBOUND,
    FILTER_CALLTYPEDIRECTION_EXTERNAL_OUTBOUND,
    FILTER_CALLTYPEDIRECTION_EXTERNAL_TRKTOTRK,
    FILTER_CALLTYPEDIRECTION_INBOUND,
    FILTER_CALLTYPEDIRECTION_INTERNAL_ALL,
    FILTER_CALLTYPEDIRECTION_INTERNAL_INBOUND,
    FILTER_CALLTYPEDIRECTION_INTERNAL_OUTBOUND,
    FILTER_CALLTYPEDIRECTION_OUTBOUND,
    FILTER_CALLTYPEDIRECTION_UNKNOWNORINITIATING,
    FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY,
    FILTER_CHANNEL_TYPE_FILTER_OPTION_EMAIL,
    FILTER_CHANNEL_TYPE_FILTER_OPTION_VOICE,
    FILTER_CLITYPE_NONE,
    FILTER_CLITYPE_RECEIVED
} from './Constants'

import { LanguageContext } from '../../Common/Contexts'

import { MiscellaneousFiltering as MiscellaneousFilteringLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { ComboBoxAlt } from '../../Common/Components/ComboBox/ComboBoxAlt'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import AdvancedModeContext, { AdvancedModeContextType } from '../../Common/Contexts/AdvancedModeContext'
import { GenericFilterEntityItem, RptFilterEntityInfo } from '../../Common/Types/Types'
import { TELEPHONY_SYSTEM_TYPE_MS_TEAMS } from '../Constants'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    channelTypes: number[]
    setChannelTypes: React.Dispatch<React.SetStateAction<number[]>>
    callTypeDirection: number
    setCallTypeDirection: (newValue: number) => void
    answerType: number
    setAnswerType: (newValue: number) => void
    callerID: number
    setCallerID: (newValue: number) => void
    DDIDigits: string
    setDDIDigits: (newValue: string) => void
    accountCodes: string
    setAccountCodes: (newValue: string) => void
    callIdentifier: string
    setCallIdentifier: (newValue: string) => void
    subjectFilter: string
    setSubjectFilter: (newValue: string) => void
    treatConfCallAsExternal: boolean
    setTreatConfCallAsExternal: (checked: boolean) => void
    supportsSubjectFiltering: boolean
    isReportTemplateUser: boolean
    currentTelSysType?: number
}

/**
 * Miscellaneous filtering section in filter tab.
 * @param filterEntityInfo The list of entities according to the filter.
 * @param channelTypes Report property.
 * @param setChannelTypes Setter for corresponding report property.
 * @param callTypeDirection Report property.
 * @param setCallTypeDirection Setter for corresponding report property.
 * @param answerType Report property.
 * @param setAnswerType Setter for corresponding report property.
 * @param callerID Report property.
 * @param setCallerID Setter for corresponding report property.
 * @param DDIDigits Report property.
 * @param setDDIDigits Setter for corresponding report property.
 * @param accountCodes Report property.
 * @param setAccountCodes Setter for corresponding report property.
 * @param callIdentifier Report property.
 * @param setCallIdentifier Setter for corresponding report property.
 * @param subjectFilter Report property.
 * @param setSubjectFilter Setter for corresponding report property.
 * @param treatConfCallAsExternal Report property.
 * @param setTreatConfCallAsExternal Setter for corresponding report property.
 * @param supportsSubjectFiltering Whether the currently selected report style supports subject filtering.
 */
const MiscellaneousFiltering: React.FC<Props> = ({
    filterEntityInfo,
    channelTypes,
    setChannelTypes,
    callTypeDirection,
    setCallTypeDirection,
    answerType,
    setAnswerType,
    callerID,
    setCallerID,
    DDIDigits,
    setDDIDigits,
    accountCodes,
    setAccountCodes,
    callIdentifier,
    setCallIdentifier,
    subjectFilter,
    setSubjectFilter,
    treatConfCallAsExternal,
    setTreatConfCallAsExternal,
    supportsSubjectFiltering,
    isReportTemplateUser,
    currentTelSysType
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('MiscellaneousFiltering')

    const { isAdvancedMode }: AdvancedModeContextType = useContext(AdvancedModeContext)

    const channelTypeSelectOptions: SelectOptions<number> = useMemo(
        (): SelectOptions<number> =>
            [
                {
                    value: FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY,
                    label: language.get(MiscellaneousFilteringLanguage.ChanneltypesOptionAny),
                    enabled: true
                },
                {
                    value: FILTER_CHANNEL_TYPE_FILTER_OPTION_VOICE,
                    label: language.get(MiscellaneousFilteringLanguage.ChanneltypesOptionVoice),
                    enabled: true
                }
            ].filter((option) => option.enabled),
        []
    )

    const callTypeDirectionSelectOptions: SelectOptions<number> = useMemo(
        () => [
            {
                value: ANY_VALUE_NUMBER_NUMERIC,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionAny)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_UNKNOWNORINITIATING,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionUnknownorinitiating)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_INBOUND,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionInbound)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_OUTBOUND,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionOutbound)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_EXTERNAL_ALL,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionExternalAll)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_EXTERNAL_INBOUND,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionExternalInbound)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_EXTERNAL_OUTBOUND,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionExternalOutbound)
            },
            ...(currentTelSysType === TELEPHONY_SYSTEM_TYPE_MS_TEAMS
                ? []
                : [
                      {
                          value: FILTER_CALLTYPEDIRECTION_EXTERNAL_TRKTOTRK,
                          label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionExternalTrktotrk)
                      }
                  ]),
            {
                value: FILTER_CALLTYPEDIRECTION_INTERNAL_ALL,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionInternalAll)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_INTERNAL_INBOUND,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionInternalInbound)
            },
            {
                value: FILTER_CALLTYPEDIRECTION_INTERNAL_OUTBOUND,
                label: language.get(MiscellaneousFilteringLanguage.CalltypedirectionOptionInternalOutbound)
            }
        ],
        [currentTelSysType]
    )

    const answerTypeSelectOptions: SelectOptions<number> = useMemo(
        () => [
            {
                value: ANY_VALUE_NUMBER_NUMERIC,
                label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionAny)
            },
            {
                value: FILTER_ANSWER_TYPE_NOT_ANSWERED,
                label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionNotans)
            },
            {
                value: FILTER_ANSWER_TYPE_ANSWERED,
                label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionAns)
            },
            {
                value: FILTER_ANSWER_TYPE_ABANDONED,
                label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionAbandoned)
            },
            ...(currentTelSysType === TELEPHONY_SYSTEM_TYPE_MS_TEAMS
                ? []
                : [
                      {
                          value: FILTER_ANSWER_TYPE_ADVANCED_FROM,
                          label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionAdvancedFrom)
                      },
                      {
                          value: FILTER_ANSWER_TYPE_ADVANCED_TO,
                          label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionAdvancedTo)
                      }
                  ]),
            {
                value: FILTER_ANSWER_TYPE_OVERFLOWED_FROM,
                label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionOverflowedFrom)
            },
            {
                value: FILTER_ANSWER_TYPE_OVERFLOWED_TO,
                label: language.get(MiscellaneousFilteringLanguage.AnswertypeOptionOverflowedTo)
            }
        ],
        [currentTelSysType]
    )

    const callerIDSelectOptions: SelectOptions<number> = useMemo(
        () => [
            {
                value: ANY_VALUE_NUMBER_NUMERIC,
                label: language.get(MiscellaneousFilteringLanguage.ClicalleridreceivedAny)
            },
            {
                value: FILTER_CLITYPE_NONE,
                label: language.get(MiscellaneousFilteringLanguage.ClicalleridreceivedNone)
            },
            {
                value: FILTER_CLITYPE_RECEIVED,
                label: language.get(MiscellaneousFilteringLanguage.ClicalleridreceivedReceived)
            }
        ],
        []
    )

    /**
     * handles channel types select box changes. It deselects the '[Any]' item in case one of the normal was selected
     * and automatically selects '[Any]' if all items were unselected
     * @param newValue New selected value.
     */
    const handleChannelTypesChange = (newValue: number[]): void => {
        setChannelTypes((prevValue: number[]): number[] => {
            const prevValueHasAnyOptionSelected = prevValue.indexOf(FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY) !== -1
            const newValueHasAnyOptionSelected = newValue.indexOf(FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY) !== -1
            const anyOptionNewlySelected = !prevValueHasAnyOptionSelected && newValueHasAnyOptionSelected

            if (newValue.length === 0 || anyOptionNewlySelected) {
                return [FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY]
            }

            return newValue.filter((value) => value !== FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY)
        })
    }

    /**
     * Calculates whether the subject filter input should be disabled.
     */
    const isSubjectFilterDisabled = (): boolean =>
        channelTypes.find(
            (option) => option === FILTER_CHANNEL_TYPE_FILTER_OPTION_EMAIL || option === ANY_VALUE_NUMBER_NUMERIC
        ) === undefined

    const toEntityOptions = (entityContainerItem: GenericFilterEntityItem | undefined): SelectRangeItem[] =>
        entityContainerItem
            ? entityContainerItem.Data.map((entity) => ({
                  value: entity.ID,
                  renderElement: (
                      <div>
                          {entity.ID} - {entity.Description}
                      </div>
                  )
              }))
            : []

    return (
        <Fieldset className='misc-filtering'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(MiscellaneousFilteringLanguage.MiscellaneousFiltering_SectionHeader)}
                <HelpTag
                    className='configure-report-help-title-tag'
                    helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_MISC_FILTERING}
                    tag={Tags.CONFIGURE_REPORT}
                />
            </Heading>

            <FormRow className={'channel-types'}>
                <SelectMulti
                    label={language.get(MiscellaneousFilteringLanguage.ChanneltypesLabel)}
                    value={channelTypes}
                    handleChange={handleChannelTypesChange}
                    options={channelTypeSelectOptions}
                />
            </FormRow>
            {supportsSubjectFiltering && isAdvancedMode && (
                <FormRow className={'subject'}>
                    <TextInput
                        value={subjectFilter}
                        onChange={setSubjectFilter}
                        labelLeft={language.get(MiscellaneousFilteringLanguage.SubjectfilterLabel)}
                        disabled={isSubjectFilterDisabled()}
                    />
                </FormRow>
            )}
            <FormRow className={'call-type-direction'}>
                <ComboBoxAlt
                    label={language.get(MiscellaneousFilteringLanguage.CalltypedirectionLabel)}
                    value={callTypeDirection}
                    handleChange={(newValue?: number | number[]): void => {
                        if (!IsVarEmpty(newValue) && !Array.isArray(newValue)) {
                            setCallTypeDirection(newValue)
                        } else if (Array.isArray(newValue) && newValue.length > 0) {
                            setCallTypeDirection(newValue[0])
                        }
                    }}
                    options={callTypeDirectionSelectOptions}
                />
            </FormRow>
            {isAdvancedMode ? (
                <FormRow className={'treat-conf-as-external'}>
                    <div className='tabbed-field'>
                        <span className='spacer' />
                        <Checkbox
                            onChange={setTreatConfCallAsExternal}
                            isChecked={treatConfCallAsExternal}
                            labelLeft={language.get(MiscellaneousFilteringLanguage.TreatconfcallasExternalLabel)}
                        />
                    </div>
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow className={'answer-type'}>
                <ComboBoxAlt
                    label={language.get(MiscellaneousFilteringLanguage.AnswertypeLabel)}
                    value={answerType}
                    handleChange={(newValue?: number | number[]): void => {
                        if (!IsVarEmpty(newValue) && !Array.isArray(newValue)) {
                            setAnswerType(newValue)
                        } else if (Array.isArray(newValue) && newValue.length > 0) {
                            setAnswerType(newValue[0])
                        }
                    }}
                    options={answerTypeSelectOptions}
                />
            </FormRow>
            {isAdvancedMode && !isReportTemplateUser ? (
                <>
                    <FormRow className={'cli-caller-id-received'}>
                        <Select
                            label={language.get(MiscellaneousFilteringLanguage.ClicalleridreceivedLabel)}
                            value={callerID}
                            handleChange={setCallerID}
                            options={callerIDSelectOptions}
                        />
                    </FormRow>
                    <FormRow className={'ddi-digits'}>
                        <SelectRange
                            items={toEntityOptions(filterEntityInfo?.EntityInfo.DevicesRouting)}
                            rangeExpressionValue={DDIDigits}
                            onValueChange={setDDIDigits}
                            specialAnyItemIndex={0}
                            labelLeft={language.get(MiscellaneousFilteringLanguage.DiddigitsLabel)}
                        />
                    </FormRow>
                    <FormRow className={'account-codes'}>
                        <SelectRange
                            items={toEntityOptions(filterEntityInfo?.EntityInfo.CodesAccount)}
                            rangeExpressionValue={accountCodes}
                            onValueChange={setAccountCodes}
                            specialAnyItemIndex={0}
                            labelLeft={language.get(MiscellaneousFilteringLanguage.AccountcodesLabel)}
                        />
                    </FormRow>
                    <FormRow className={'call-identifier'}>
                        <TextInput
                            value={callIdentifier}
                            onChange={setCallIdentifier}
                            labelLeft={language.get(MiscellaneousFilteringLanguage.CallidentifierLabel)}
                            placeholder={language.get(
                                MiscellaneousFilteringLanguage.MiscellaneousFiltering_AnyPlaceholder
                            )}
                        />
                    </FormRow>
                </>
            ) : (
                <></>
            )}
        </Fieldset>
    )
}

export default React.memo(MiscellaneousFiltering)
